import type { Alignment, Config, Driver, Side } from 'driver.js';

import { createPortal } from 'react-dom';
import {
  calculateLeftForTopBottom,
  calculateTopForLeftRight,
  getPopoverDimensions,
  renderPopoverArrow,
} from './popover-utils';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { getStepElement } from './stage';
import { StyledStepWrapper } from './styles';
import type { TourStep } from './types';

export const renderPopover: Config['onPopoverRender'] = ({ wrapper }, { driver }) => {
  const portal = createPortal(
    <StyledStepWrapper between gap="u8" className="driver-popover-wrapper">
      <StepBody driver={driver} />
      <StepFooter driver={driver} />
    </StyledStepWrapper>,
    wrapper,
  );
  driver.setPopover(portal);
};

export const repositionPopover = (driver: Driver, step: TourStep) => {
  const element = getStepElement(step);
  const popover = document.querySelector('.driver-popover') as HTMLElement;
  if (!element || !popover) {
    return;
  }

  popover.style.opacity = '0';

  // Configure the popover positioning
  const requiredAlignment: Alignment = 'start';
  const requiredSide: Side = element.id === 'driver-dummy-element' ? 'over' : 'left';
  const popoverPadding = driver.getConfig().stagePadding || 0;

  const popoverDimensions = getPopoverDimensions(driver);
  if (!popoverDimensions) {
    return;
  }

  const popoverArrow = document.querySelector('.driver-popover-arrow');
  if (!popoverArrow) {
    return;
  }

  const popoverArrowDimensions = popoverArrow.getBoundingClientRect();
  const elementDimensions = element.getBoundingClientRect();

  const topValue = elementDimensions.top - popoverDimensions.height;
  let isTopOptimal = topValue >= 0;

  const bottomValue = window.innerHeight - (elementDimensions.bottom + popoverDimensions.height);
  let isBottomOptimal = bottomValue >= 0;

  const leftValue = elementDimensions.left - popoverDimensions.width;
  const isLeftOptimal = leftValue >= 0;

  const rightValue = window.innerWidth - (elementDimensions.right + popoverDimensions.width);
  let isRightOptimal = rightValue >= 0;

  const noneOptimal = !isTopOptimal && !isBottomOptimal && !isLeftOptimal && !isRightOptimal;
  let popoverRenderedSide: Side = requiredSide;

  if (requiredSide === 'left' && isLeftOptimal) {
    isRightOptimal = isTopOptimal = isBottomOptimal = false;
  }

  if (requiredSide === 'over') {
    const leftToSet = window.innerWidth / 2 - popoverDimensions.realWidth / 2;
    const topToSet = window.innerHeight / 2 - popoverDimensions.realHeight / 2;

    popover.style.left = `${leftToSet}px`;
    popover.style.right = `auto`;
    popover.style.top = `${topToSet}px`;
    popover.style.bottom = `auto`;
  } else if (noneOptimal) {
    const leftValue = window.innerWidth / 2 - popoverDimensions.realWidth / 2;
    const bottomValue = 10;

    popover.style.left = `${leftValue}px`;
    popover.style.right = `auto`;
    popover.style.bottom = `${bottomValue}px`;
    popover.style.top = `auto`;
  } else if (isLeftOptimal) {
    const leftToSet = Math.min(
      leftValue,
      window.innerWidth - popoverDimensions?.realWidth - popoverArrowDimensions.width,
    );

    const topToSet = calculateTopForLeftRight(requiredAlignment, {
      elementDimensions,
      popoverDimensions,
      popoverPadding,
      popoverArrowDimensions,
    });

    popover.style.left = `${leftToSet}px`;
    popover.style.top = `${topToSet}px`;
    popover.style.bottom = `auto`;
    popover.style.right = 'auto';

    popoverRenderedSide = 'left';
  } else if (isRightOptimal) {
    const rightToSet = Math.min(
      rightValue,
      window.innerWidth - popoverDimensions?.realWidth - popoverArrowDimensions.width,
    );
    const topToSet = calculateTopForLeftRight(requiredAlignment, {
      elementDimensions,
      popoverDimensions,
      popoverPadding,
      popoverArrowDimensions,
    });

    popover.style.right = `${rightToSet}px`;
    popover.style.top = `${topToSet}px`;
    popover.style.bottom = `auto`;
    popover.style.left = 'auto';

    popoverRenderedSide = 'right';
  } else if (isTopOptimal) {
    const topToSet = Math.min(
      topValue,
      window.innerHeight - popoverDimensions.realHeight - popoverArrowDimensions.width,
    );
    const leftToSet = calculateLeftForTopBottom(requiredAlignment, {
      elementDimensions,
      popoverDimensions,
      popoverPadding,
      popoverArrowDimensions,
    });

    popover.style.top = `${topToSet}px`;
    popover.style.left = `${leftToSet}px`;
    popover.style.bottom = `auto`;
    popover.style.right = 'auto';

    popoverRenderedSide = 'top';
  } else if (isBottomOptimal) {
    const bottomToSet = Math.min(
      bottomValue,
      window.innerHeight - popoverDimensions?.realHeight - popoverArrowDimensions.width,
    );

    const leftToSet = calculateLeftForTopBottom(requiredAlignment, {
      elementDimensions,
      popoverDimensions,
      popoverPadding,
      popoverArrowDimensions,
    });

    popover.style.left = `${leftToSet}px`;
    popover.style.bottom = `${bottomToSet}px`;
    popover.style.top = `auto`;
    popover.style.right = 'auto';

    popoverRenderedSide = 'bottom';
  }

  if (noneOptimal) {
    popoverArrow.classList.add('driver-popover-arrow-none');
  } else {
    renderPopoverArrow(driver, element, popover, requiredAlignment, popoverRenderedSide);
  }

  popover.style.opacity = '1';
};
